// absorb-zones-20261019: past Absorption levels as supply/demand zones.
// Purely additive drawing — never touches the live absorption cells, Liq
// labels or signals. Same per-candle detection as the Absorption memo (one
// strongest cell per closed candle), run over the LOADED history.
//
// Rules:
//   event   = strongest absorption cell per closed candle (same score as the
//             indicator); strength = the absorbed side's volume at that level.
//   cluster = same side, within 1.5 rows (row = that candle's level step) of
//             an ACTIVE zone's mean price -> merged (n++, vol summed).
//   break   = a later CLOSE beyond the zone (bid zone: close < lo,
//             ask zone: close > hi) -> zone ends at that candle.
//   keep    = avg strength (vol / n) >= 1.5x the mean avg of all zones.

const STEP_TOL = 1.5
const KEEP_MULT = 1.5
const MAX_IDLE = 30     // abszone-end-20261031: no new touch for 30 candles -> zone ends
const SKIP_FIRST = 2    // first loaded candles (launch / history edge) never seed a zone

export function computeAbsorbZones(candles, forEachLevel, minMult) {
  const out = []
  if (!Array.isArray(candles) || candles.length < 3) return out
  const last = candles.length - 1   // forming candle excluded
  const vols = []
  for (let i = last - 1; i >= 0 && vols.length < 20000; i--) {
    const c = candles[i]
    if (!c || (c.isHistorical && !c._omFootprint)) continue
    forEachLevel(c, (m, b, s) => { const t = b + s; if (t > 0) vols.push(t) })
  }
  if (vols.length < 8) return out
  vols.sort((a, b) => a - b)
  const median = vols[vols.length >> 1] || 0
  if (!(median > 0)) return out
  const minVol = median * (minMult || 3)
  const zones = [], active = []
  for (let i = 0; i < last; i++) {
    const c = candles[i]
    if (!c) continue
    const close = c.close_mcap
    // break check first (a candle can't break the zone it forms)
    if (Number.isFinite(close)) {
      for (let k = active.length - 1; k >= 0; k--) {
        const z = active[k]
        if ((z.side === 'bid' && close < z.lo) || (z.side === 'ask' && close > z.hi) || i - z.last > MAX_IDLE) {
          z.end = i; active.splice(k, 1)
        }
      }
    }
    if (c.isHistorical && !c._omFootprint) continue
    if (i < SKIP_FIRST) continue
    let hi = -Infinity, lo = Infinity, prev = null, step = Infinity
    const lv = []
    forEachLevel(c, (m, b, s) => { lv.push(m, b, s) })
    for (let j = 0; j < lv.length; j += 3) {
      const m = lv[j]
      if (lv[j + 1] + lv[j + 2] > 0) { if (m > hi) hi = m; if (m < lo) lo = m }
    }
    const sorted = []
    for (let j = 0; j < lv.length; j += 3) sorted.push(lv[j])
    sorted.sort((a, b) => a - b)
    for (const m of sorted) { if (prev != null && m > prev && m - prev < step) step = m - prev; prev = m }
    // absorb-zone-row-20261024: the zone row is the candle's real price row
    // (the lattice its levels came from), NOT the gap between its populated
    // levels. Sparse candles (2-3 levels far apart) made that gap huge, and
    // one-level candles fell back to range/10 -> giant merge tolerance ->
    // unrelated events chained into one band that never broke (open to the
    // right edge). Dense candles: row == smallest gap, so unchanged.
    const rowAt = rowStepOf(c)
    if (Number.isFinite(c.high_mcap) && Number.isFinite(c.low_mcap)) { hi = c.high_mcap; lo = c.low_mcap }
    const range = hi - lo
    if (!Number.isFinite(close) || !(range > 0)) continue
    let best = 0, bm = null, bs = null, bv = 0
    for (let j = 0; j < lv.length; j += 3) {
      const m = lv[j], buy = lv[j + 1], sell = lv[j + 2]
      // abszone-20261025: m is a display/native row's LOWER EDGE; the row is
      // [m, m + rowAt(m)). A rejection needs the close OUTSIDE the absorbed
      // row — above its top for bid, below its bottom for ask. Testing the
      // bid against the lower edge (close > m) was true for any close inside
      // the same row, so on a coarse lattice every candle sitting in one row
      // (e.g. all early candles in row [0, bucket)) chained into one bid zone
      // at m = 0 whose lo (-row/2) no close can ever break: open from the
      // first candle to the right edge.
      const top = m + rowAt(m)
      if (sell >= minVol && close >= top) { const sc = sell * ((close - m) / range); if (sc > best) { best = sc; bm = m; bs = 'bid'; bv = sell } }
      if (buy >= minVol && close < m) { const sc = buy * ((m - close) / range); if (sc > best) { best = sc; bm = m; bs = 'ask'; bv = buy } }
    }
    if (bm == null) continue
    const row = rowAt(bm)
    if (row > 0 && Number.isFinite(row) && !(step <= row)) step = row
    if (!(step > 0) || !Number.isFinite(step)) continue
    const tol = step * STEP_TOL
    let z = null
    for (const a of active) {
      if (a.side !== bs || Math.abs(a.sum / a.n - bm) > tol) continue
      z = a; break
    }
    if (z) {
      z.n++; z.vol += bv; z.sum += bm; z.last = i
      z.lo = Math.min(z.lo, bm - z.step / 2); z.hi = Math.max(z.hi, bm + z.step / 2)
    } else {
      z = { side: bs, start: i, end: null, n: 1, vol: bv, sum: bm, step, last: i, lo: bm - step / 2, hi: bm + step / 2 }
      zones.push(z); active.push(z)
    }
  }
  if (!zones.length) return out
  let mean = 0
  for (const z of zones) mean += z.vol / z.n
  mean /= zones.length
  for (const z of zones) if (z.vol > 0 && z.vol / z.n >= mean * KEEP_MULT) out.push(z)
  return out
}

// Row height at price m for the lattice forEachLevel(c) reads unkeyed:
// _display first (linear bucket or log ratio), else storage (_nativeBucket).
function rowStepOf(c) {
  if (c._display && c._display.length > 0) {
    const r = c._displayRatio
    if (r > 1) return (m) => m * (r - 1)
    const b = c._displayBucket
    return () => b
  }
  const b = c._nativeBucket
  return () => b
}

export function hexA(hex, a) {
  const h = String(hex || '').replace('#', '')
  if (h.length < 6) return `rgba(255,209,102,${a})`
  return `rgba(${parseInt(h.slice(0, 2), 16)},${parseInt(h.slice(2, 4), 16)},${parseInt(h.slice(4, 6), 16)},${a})`
}
